import { PermissaoDescricao } from '../types/graphql';

export function removeDuplicated<T>(arr: Array<T>) {
  return arr.filter((element, index) => arr.indexOf(element) === index);
}

function captalize(name: string) {
  return name[0].toUpperCase() + name.slice(1).toLowerCase();
}

export function snackCaseToCamelWithSpace(perm: string) {
  return perm
    .split('_')
    .map(captalize)
    .join(' ');
}

enum Groups {
  ENTIDADE = 'Entidade',
  FUNCIONARIO = 'Funcionário',
  CLIENTE = 'Cliente',
  PEDIDO_REEMBOLSO = 'Pedido de Reembolso',
  CONTA_PAGAR = 'Conta a Pagar',
  EMPRESTIMO = 'Empréstimo',
  GRUPO_PERMISSOES = 'Grupo de Permissões',
  RELATORIO = 'Relatório',
  META = 'Metas',
  LEADS = 'Leads',
  TAREFAS = 'Tarefas CRM',
  ANOTACOES_HISTORICO = 'Histórico CRM',
  ZEKI = 'Zeki',
  CONFIGURACOES = 'Configurações',
}

type GroupsKey = keyof typeof Groups;

type ActionDescType = { displayName: string; action: PermissionAction };
type GroupDescType = { displayName: string; group: GroupsKey; permissions: ActionDescType[] };

const PermissonDisplayNames = {
  FECHAMENTO_CAIXA_RELATORIO: 'Fechamento de Caixa',
  EDITAR_LOJA_CONTA_PAGAR: 'Editar Loja',
  DEFINIR_TAXA_SEGURANCA_ENTIDADE: 'Definir Taxa de Segurança',
  DEFINIR_TAXA_MINIMA_ENTIDADE: 'Definir Taxa Mínima',
  DEFINIR_TAXA_LUCRO_ENTIDADE: 'Definir Taxa de Lucro',
  VER_ANOTACOES_HISTORICO: 'Ver Histórico e Anotações',
  CRIAR_ANOTACOES_HISTORICO: 'Criar Anotações',
  EDITAR_ANOTACOES_HISTORICO: 'Editar Anotações',
  EXCLUIR_ANOTACOES_HISTORICO: 'Excluir Anotações',
  VER_PARCEIRO: 'Ver Parceiros',
  CRIAR_PARCEIRO: 'Criar Parceiros',
  EDITAR_PARCEIRO: 'Editar Parceiros',
  VER_TABELA_PARCEIRO: 'Ver Tabelas',
  CRIAR_TABELA_PARCEIRO: 'Criar Tabelas',
  EDITAR_TABELA_PARCEIRO: 'Editar Tabelas',
  DASHBOARD_PARCEIRO: 'Dashboard Parceiro',
  SOLICITAR_RESGATE_PARCEIRO: 'Solicitar Resgate',
  HISTORICO_RESGATE_PARCEIRO: 'Histórico de Resgate',
  HISTORICO_RESGATE_FUNCIONARIO_PARCEIRO: 'Histórico de Resgate Funcionário',
  EMPRESTIMO_CLIENTE_RELATORIO: 'Cliente x Empréstimo',
  VER_MENSAGEM_SMS: 'Ver Mensagem SMS',
  CRIAR_MENSAGEM_SMS: 'Criar Mensagem SMS',
  EDITAR_STATUSPAGO_EMPRESTIMO: 'Editar com status pago',
  SMS_RELATORIO: 'SMS/TXT',
  VER_BOT_ZEKI: 'Ver Bot',
  EDITAR_BOT_ZEKI: 'Editar Bot',
  REGRAMENTO_ENTIDADE_CONFIGURACOES: 'Regramento por Loja',
  EXCLUIR_FUNCIONARIO: 'Excluir funcionario',
};

const PermissionGroup: Partial<Record<PermissaoDescricao, keyof typeof Groups>> = {
  EMPRESTIMO_CLIENTE_RELATORIO: 'RELATORIO',
  REGRAMENTO_ENTIDADE_CONFIGURACOES: 'CONFIGURACOES',
};

const ActionDisplayNames = {
  EDITAR_LOJA_CONTA_PAGAR: 'EDITAR_LOJA',
  VER_MENSAGEM_SMS: 'VER_MENSAGEM',
  CRIAR_MENSAGEM_SMS: 'CRIAR_MENSAGEM',
  DEFINIR_TAXA_SEGURANCA_ENTIDADE: 'DEFINIR_TAXA_SEGURANCA',
  DEFINIR_TAXA_MINIMA_ENTIDADE: 'DEFINIR_TAXA_MINIMA',
  DEFINIR_TAXA_LUCRO_ENTIDADE: 'DEFINIR_TAXA_LUCRO',
  VER_TABELA_PARCEIRO: 'VER_TABELA',
  CRIAR_TABELA_PARCEIRO: 'CRIAR_TABELA',
  EDITAR_TABELA_PARCEIRO: 'EDITAR_TABELA',
  SOLICITAR_RESGATE_PARCEIRO: 'SOLICITAR_RESGATE',
  HISTORICO_RESGATE_PARCEIRO: 'HISTORICO_RESGATE',
  HISTORICO_RESGATE_FUNCIONARIO_PARCEIRO: 'HISTORICO_RESGATE_FUNCIONARIO',
  DASHBOARD_FUNCIONARIO_PARCEIRO: 'DASHBOARD_FUNCIONARIO',
  EMPRESTIMO_CLIENTE_RELATORIO: 'EMPRESTIMO_CLIENTE',
  FECHAMENTO_CAIXA_RELATORIO: 'FECHAMENTO_CAIXA',
  EDITAR_STATUSPAGO_EMPRESTIMO: 'EDITAR_STATUSPAGO',
  VER_BOT_ZEKI: 'VER_BOT',
  EDITAR_BOT_ZEKI: 'EDITAR_BOT',
  REGRAMENTO_ENTIDADE_CONFIGURACOES: 'REGRAMENTO_ENTIDADE',
};

export enum PermissionAction {
  VER = 'VER',
  CRIAR = 'CRIAR',
  EDITAR = 'EDITAR',
  EXCLUIR = 'EXCLUIR',
  SIMULAR = 'SIMULAR',
  DEFINIR_TAXA = 'DEFINIR_TAXA',
  FECHAMENTO = 'FECHAMENTO_CAIXA',
}

function permissionDisplayName(description: string): string {
  return (
    PermissonDisplayNames[description as keyof typeof PermissonDisplayNames] ??
    snackCaseToCamelWithSpace(description.split('_')[0])
  );
}

function permissionActionName(description: string): string {
  return ActionDisplayNames[description as keyof typeof ActionDisplayNames] ?? description.split('_')[0];
}

function groupPermissions(permissionsDescriptions: PermissaoDescricao[]): GroupDescType[] {
  const groups = Object.keys(Groups) as Array<keyof typeof Groups>;
  const x = groups.map(group => {
    const permissions = permissionsDescriptions
      .filter(description => {
        if (Object.keys(PermissionGroup).includes(description) && String(PermissionGroup[description]) !== group) {
          return false;
        }
        return description.includes(group);
      })
      .map<ActionDescType>(description => ({
        action: (permissionActionName(description) as unknown) as PermissionAction,
        displayName: permissionDisplayName(description),
      }));
    return { group, displayName: Groups[group], permissions };
  });
  return x;
}

export function useGroupedPermissions() {
  return groupPermissions(Object.values(PermissaoDescricao)).map(e => ({
    group: e.group,
    displayName: e.displayName,
    permissions: e.permissions.filter(
      e =>
        e.displayName !== 'SMS/TXT' &&
        e.displayName !== 'Histórico de Resgate Funcionário' &&
        e.displayName !== 'Excluir funcionario',
    ),
  }));
}
